<script lang="ts" setup>
import { BusEvent, HpStep } from '@/helper/Types.ts'
import appStore from '@/store/AppStore.ts'
import { StepsController } from '@/controllers/StepsController.ts'
import bus from '@/helper/Bus.ts'
import LanguageSwitcher from '@/components/header/LanguageSwitcher.vue'
import { onMounted, onUnmounted, onUpdated, ref } from 'vue'
import { isDesktop } from '@/helper/tools.ts'
import userStore from '@/store/UserStore.ts'

defineProps<{
  stepName?: string
}>()

defineEmits<{
  (e: 'btnOverviewClicked'): void
}>()

const COMPONENT_NAME = 'StepsHeader'

const isScrolledRight = ref<boolean>(false)
const isOverflow = ref<boolean>(false)
const menuOpen = ref<boolean>(false)

const navigationIndicator = ref<HTMLSpanElement | null>(null)
const headerContainer = ref<HTMLDivElement | null>(null)
const stepsContainer = ref<HTMLDivElement | null>(null)
const nav = ref<HTMLUListElement | null>(null)
const el = ref<HTMLElement | null>(null)

bus.$onC(COMPONENT_NAME, BusEvent.LANGUAGE_CHANGED, () => {
  stepChangedHandler()
})
bus.$onC(COMPONENT_NAME, BusEvent.WINDOW_RESIZE, () => {
  isDesktop() ? toggleMenu(false) : doShift(false)
  stepChangedHandler()
  checkOverflow()
})

onMounted((): void => {
  const $indicator = navigationIndicator.value as HTMLElement

  bus.$onC(COMPONENT_NAME, BusEvent.LOAD_PROJECT_FINISHED, () =>
    setTimeout(() => {
      $indicator.classList.add('is--animated')
    }, 100)
  )
  bus.$onC(COMPONENT_NAME, BusEvent.STEP_CHANGED, stepChangedHandler)
  stepChangedHandler()
})

onUpdated((): void => {
  checkOverflow()
})

onUnmounted((): void => {
  bus.$offC(COMPONENT_NAME)
})

function toggleMenu(open: boolean | null = null): void {
  const body = document.body
  if (open) {
    menuOpen.value = true
    if (body) {
      body.classList.add('menu-open')
    }
  } else if (open === false) {
    menuOpen.value = false
    if (body) {
      body.classList.remove('menu-open')
    }
  } else {
    if (body) {
      menuOpen.value = body.classList.toggle('menu-open')
    }
  }
}

function isStep(stepIndex: number): boolean {
  return appStore.currentStep === appStore.steps[stepIndex].step
}

function isAllowed(stepIndex: number): boolean {
  return stepIndex === 0 || appStore.steps[stepIndex - 1].isValid
}

function stepClicked(e: Event, step: HpStep): void {
  StepsController.jumpToStep(step)
  const $clickedStepItem = (e.currentTarget as HTMLAnchorElement).parentNode as HTMLLIElement
  if (!$clickedStepItem.classList.contains('is--allowed')) {
    return
  }
  toggleMenu(false)
}

function menuButtonClicked(): void {
  toggleMenu()
}

function checkOverflow(): void {
  isOverflow.value = (stepsContainer.value?.clientWidth || 0) < (stepsContainer.value?.scrollWidth || 0)
}

function stepChangedHandler(): void {
  //Timeout to wait for loading data like translations and text to set the position right
  setTimeout(() => {
    if (appStore.currentStep === HpStep.EMPTY || !navigationIndicator.value) {
      return
    }
    const $currStepElement =
      el.value?.querySelectorAll('.navigation__item')[appStore.getStepIndex(appStore.currentStep)]
    const $indicator = navigationIndicator.value as HTMLElement
    const posLeft = ($currStepElement as HTMLElement).offsetLeft
    const width = ($currStepElement as HTMLElement).offsetWidth

    $indicator.style.transform = `translate3d(${posLeft}px, 0, 0)`
    $indicator.style.width = `${width}px`

    if (stepsContainer.value) {
      isScrolledRight.value = stepsContainer.value.clientWidth < posLeft + width && isDesktop()
    }

    doShift(isScrolledRight.value)
  }, 50)
}

function doShift(isRight: boolean): void {
  isScrolledRight.value = isRight

  if (stepsContainer.value && nav.value) {
    const shift = stepsContainer.value.clientWidth - nav.value.clientWidth - 48
    nav.value.style.transform = isRight ? `translate3d(${shift}px,0,0)` : ''
  }
}
</script>

<template>
  <div ref="el" class="header steps-header">
    <div ref="headerContainer" class="container header__container">
      <button v-if="!userStore.planningSuiteToken" class="btn-back icon-text" @click="$emit('btnOverviewClicked')">
        <i class="ic-arrow-back" />
        <span class="hide-mobile">{{ $t('global.btn_back_to_overview') }}</span>
      </button>
      <nav>
        <div
          ref="stepsContainer"
          :class="{ 'is--scrolledright': isScrolledRight, 'is--overflow': isOverflow }"
          class="steps__container"
        >
          <button class="btn-plain btn-left" @click="doShift(false)">
            <i class="ic-chevron-left" />
          </button>

          <button class="btn-plain btn-right" @click="doShift(true)">
            <i class="ic-chevron-right" />
          </button>

          <ul ref="nav" class="steps__navigation navigation" tabindex="1">
            <li
              v-for="(step, i) in appStore.steps"
              :key="i"
              :class="{
                'is--active': isStep(i),
                'is--valid': step.isValid,
                'is--allowed': isAllowed(i)
              }"
              class="navigation__item"
            >
              <a :accesskey="i + 1" href="#" @click.prevent="stepClicked($event, step.step)">
                <span class="navigation__label" />{{ $t(step.name) }}
                <i class="navigation__arrow ic-chevron-right hide-desktop" />
              </a>
            </li>

            <span ref="navigationIndicator" class="navigation__indicator hide-mobile" />
            <LanguageSwitcher class="hide-desktop lang-switcher" />
          </ul>
        </div>
      </nav>

      <h2 v-if="stepName" class="h2 header__title">
        {{ $t(stepName) }}
      </h2>

      <LanguageSwitcher class="hide-mobile" />

      <button class="header__menu-button btn-plain hide-desktop" tabindex="2" @click="menuButtonClicked">
        <span class="hamburger">
          <span />
          <span />
          <span />
        </span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.header {
  &__menu-button {
    cursor: pointer;
    height: 100%;
    padding: 0 rem-calc($pagePaddingMobile);
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;
  }
}

nav {
  @include media('>=desktop') {
    position: absolute;
    left: 0;
    right: 0;
    padding: 0 rem-calc(48);
    top: rem-calc(96);
    display: flex;
    flex-wrap: nowrap;
  }
}

.lang-switcher.hide-desktop {
  position: absolute;
  bottom: rem-calc(16);
  left: 50%;
  transform: translateX(-50%);
}
</style>
